import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {toast} from 'react-toastify';
import {useSearchParams, useNavigate} from 'react-router-dom';
// @ts-ignore
import {EnvelopeSimpleOpen, BellSlash, Warning, CircleNotch} from '@phosphor-icons/react';
import {getAxiosBaseUrl} from '../../config';

const Card = ({children}) => (
	<div className="max-w-md mx-auto rounded-2xl shadow-sm border border-slate-100 bg-white p-8">
		{children}
	</div>
);

const IconBubble = ({tone = 'emerald', children}) => {
	const palette = tone === 'amber'
		? 'bg-amber-50 border-amber-200'
		: tone === 'rose'
			? 'bg-rose-50 border-rose-200'
			: 'bg-emerald-50 border-emerald-200';
	return (
		<div className={`w-16 h-16 rounded-2xl ${palette} border flex items-center justify-center mx-auto mb-4`}>
			{children}
		</div>
	);
};

const UnSubscribe = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const email = (searchParams.get('email') || '').trim();

	// states: 'no-email' | 'confirm' | 'submitting' | 'success'
	const [state, setState] = useState(email ? 'confirm' : 'no-email');

	useEffect(() => {
		// keep state in sync if the URL changes after mount
		if (!email && state !== 'no-email') setState('no-email');
	}, [email, state]);

	const handleUnsubscribe = async () => {
		if (!email) return;
		setState('submitting');
		try {
			axios.defaults.baseURL = getAxiosBaseUrl();
			const res = await axios.post('/unsubscribe_email', {email});
			const status = res?.data?.status;
			if (status === 'success' || status === 'already_unsubscribed') {
				setState('success');
			} else {
				toast.error(res?.data?.message || "Couldn't unsubscribe — please try again.");
				setState('confirm');
			}
		} catch (err) {
			toast.error("Couldn't reach our servers. Please try again in a moment.");
			setState('confirm');
		}
	};

	const handleKeep = () => navigate('/');

	return (
		<div className="min-h-[60vh] flex items-center justify-center px-6 py-12">
			{state === 'no-email' && (
				<Card>
					<IconBubble tone="amber">
						<Warning size={32} weight="duotone" className="text-amber-500"/>
					</IconBubble>
					<h1 className="text-2xl font-semibold text-slate-900 tracking-tight text-center">
						Couldn't identify your email
					</h1>
					<p className="mt-3 text-base text-slate-600 leading-relaxed text-center">
						Please use the unsubscribe link from your most recent Neugence Marketing email.
						If you keep seeing this, contact{' '}
						<a href="mailto:contact@neugence.ai" className="text-emerald-600 hover:text-emerald-700 font-medium">
							contact@neugence.ai
						</a>
						.
					</p>
				</Card>
			)}

			{(state === 'confirm' || state === 'submitting') && (
				<Card>
					<IconBubble tone="rose">
						<BellSlash size={32} weight="duotone" className="text-rose-500"/>
					</IconBubble>
					<h1 className="text-2xl font-semibold text-slate-900 tracking-tight text-center">
						Unsubscribe from Neugence Marketing emails?
					</h1>
					<p className="mt-3 text-base text-slate-600 leading-relaxed text-center">
						We'll stop sending emails to <b className="text-slate-900">{email}</b>.
					</p>
					<div className="mt-6 space-y-3">
						<button
							type="button"
							onClick={handleUnsubscribe}
							disabled={state === 'submitting'}
							className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-3 font-semibold w-full disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
						>
							{state === 'submitting' ? (
								<>
									<CircleNotch size={18} weight="bold" className="animate-spin"/>
									Unsubscribing…
								</>
							) : (
								'Yes, unsubscribe'
							)}
						</button>
						<button
							type="button"
							onClick={handleKeep}
							disabled={state === 'submitting'}
							className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-3 font-medium w-full disabled:opacity-50 transition-colors"
						>
							Keep me subscribed
						</button>
					</div>
				</Card>
			)}

			{state === 'success' && (
				<Card>
					<IconBubble tone="emerald">
						<EnvelopeSimpleOpen size={32} weight="duotone" className="text-emerald-500"/>
					</IconBubble>
					<h1 className="text-2xl font-semibold text-slate-900 tracking-tight text-center">
						You've been unsubscribed
					</h1>
					<p className="mt-3 text-base text-slate-600 leading-relaxed text-center">
						We're sorry to see you go. <b className="text-slate-900">{email}</b> won't receive
						Neugence Marketing emails anymore.
					</p>
					<p className="mt-4 text-sm text-slate-500 leading-relaxed text-center">
						Changed your mind?{' '}
						<a href="/subscribe" className="text-emerald-600 hover:text-emerald-700 font-medium">
							Resubscribe
						</a>
						.
					</p>
				</Card>
			)}
		</div>
	);
};

export default UnSubscribe;
